import React, { useEffect, useCallback } from 'react'
import { BAlert, BCol, BRow, BText, BTitle } from '@prague-digi/ui-components'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { ReduxState } from 'app-types'
import classNames from '@prague-digi/class-names'

import { analyticsActions, modalDialogActions, paymentActions, paymentSelectors } from '../../redux'
import { ApiErrorInfo, Response } from '../../api/api'
import { BTextInput, PageControlButtons } from '../../components'
import messages from '../../i18n'
import { form, FormInterface, intl, IntlInterface, wizard, WizardInterface } from '../../hoc'
import validate from './PennyPayment.validate'
import { API_ERROR } from '../../constants/modals'

import styles from './styles.scss'

const FORM_NAME = 'PennyPayment'

interface MapDispatchToProps {
  fetchPennyPayment: Function
  setFinish: Function
  showModalDialog: Function
  submitAccountNumber: Function
}

interface MapStateToProps {
  amount: number
}

type Props = WizardInterface & IntlInterface & FormInterface & MapDispatchToProps & MapStateToProps

export const PennyPayment = ({
  amount,
  fetchPennyPayment,
  handleSubmit,
  setErrors,
  setFinish,
  showModalDialog,
  submitAccountNumber,
  translate,
  translateHTML,
  wizard,
}: Props) => {
  const onEffect = useCallback(() => {
    fetchPennyPayment({
      error: (errorInfo: ApiErrorInfo) => {
        showModalDialog(API_ERROR({ errorInfo, onRetry: onEffect }))
      },
    })
  }, [fetchPennyPayment, showModalDialog])

  useEffect(() => {
    onEffect()
  }, [onEffect])

  const onSubmit = () => {
    handleSubmit(() =>
      submitAccountNumber({
        success: () => {
          setFinish()
          wizard.gotoNext()
        },
        error: (errorInfo: ApiErrorInfo) => {
          if (errorInfo.type === Response.dataError && errorInfo.errors) {
            setErrors(FORM_NAME, errorInfo.errors)
          } else {
            showModalDialog(API_ERROR({ errorInfo, onRetry: onSubmit }))
          }
        },
      })
    )()
  }

  return (
    <>
      <BAlert noIcon theme="mb-5">
        {translate(messages.pennyPayment.notification)}
      </BAlert>
      <BTitle type="h3">{translateHTML(messages.pennyPayment.title, { amount })}</BTitle>
      <BText>{translate(messages.pennyPayment.info, { amount })}</BText>
      <BTitle type="h5">{translateHTML(messages.pennyPayment.account.title)}</BTitle>

      <BRow theme="d-flex justify-content-between">
        <BCol theme="d-flex align-items-start">
          <BTextInput
            messages={{ label: messages.pennyPayment.account.prefix }}
            name="prefix"
            testid="prefix"
            theme="w-100"
          />
        </BCol>
        <BCol
          theme={classNames(
            'd-none d-md-flex align-items-start justify-content-center col-auto',
            styles.divider
          )}>
          -
        </BCol>
        <BCol theme="d-flex align-items-start" xs={12} md={5}>
          <BTextInput
            messages={{ label: messages.pennyPayment.account.number }}
            name="number"
            testid="number"
            theme="w-100"
          />
        </BCol>
        <BCol
          theme={classNames(
            'd-none d-md-flex align-items-start justify-content-center col-auto',
            styles.divider
          )}>
          /
        </BCol>
        <BCol theme="d-flex align-items-start">
          <BTextInput
            messages={{ label: messages.pennyPayment.account.bank }}
            name="bankCode"
            testid="bankCode"
            theme="w-100"
          />
        </BCol>
      </BRow>
      <BText>{translate(messages.pennyPayment.confirm)}</BText>

      <PageControlButtons onSubmit={onSubmit} submitMessage={messages.pennyPayment.continue} />
    </>
  )
}

const mapDispatchToProps: MapDispatchToProps = {
  fetchPennyPayment: paymentActions.fetchPennyPayment,
  setFinish: analyticsActions.setFinish,
  showModalDialog: modalDialogActions.showModalDialog,
  submitAccountNumber: paymentActions.submitAccountNumber,
}

const mapStateToPros = (state: ReduxState): MapStateToProps => ({
  amount: paymentSelectors.getAmount(state),
})

const enhance = compose(
  intl,
  wizard,
  connect(mapStateToPros, mapDispatchToProps),
  form({
    name: FORM_NAME,
    validate,
  })
)

export default enhance(PennyPayment)
